/**
 * @file vertical-resize-handle.tsx
 * @description The draggable row-resize divider shown between two vertically stacked panes inside a
 * canvas view. Pairs with `useVerticalSplit` for the drag state/logic.
 */

import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils.js';

interface VerticalResizeHandleProps {
  /** Distance (0-100) from the top of the container to center the handle at. */
  topPercent: number;
  isResizing: boolean;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
}

export function VerticalResizeHandle({ topPercent, isResizing, onPointerDown }: VerticalResizeHandleProps) {
  const { t } = useTranslation();

  return (
    <div
      onPointerDown={onPointerDown}
      role="separator"
      aria-orientation="horizontal"
      title={t('Canvas.common.resize')}
      className={cn(
        'absolute left-0 right-0 z-30 group flex items-center justify-center cursor-row-resize transition-colors duration-150',
        'h-1 rounded-full',
        isResizing ? 'bg-brand-500' : 'bg-slate-300/40 dark:bg-slate-600/40 hover:bg-brand-400/50',
      )}
      style={{ top: `${topPercent}%`, transform: 'translateY(-50%)' }}
    >
      <div
        className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-row gap-1 p-1 rounded-full cursor-row-resize transition-opacity duration-150',
          isResizing
            ? 'opacity-100 bg-brand-500'
            : 'opacity-0 group-hover:opacity-100 bg-white dark:bg-slate-700 shadow-md border border-slate-200 dark:border-slate-600',
        )}
      >
        {[...Array(3)].map((_, i) => (
          <div key={i} className={cn('w-1 h-1 rounded-full', isResizing ? 'bg-white' : 'bg-slate-400 dark:bg-slate-500')} />
        ))}
      </div>
      <div className="absolute inset-x-0 -top-3 -bottom-3 cursor-row-resize z-10" />
    </div>
  );
}
